import { DirectoryAutocompleteField } from 'features';
import { lodash } from 'helpers';
import { useIsFormEditable } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { OrderSection } from 'modules/order/common/components';
import { Field, useField } from 'modules/order/common/forms/orderForm';

import { NewLabel } from '../../NewLabel';

import { SenderOrRecipientDetailsSectionProps } from './types';
import { useSenderOrRecipientDetailsSection } from './useSenderOrRecipientDetailsSection';

export const SenderOrRecipientDetailsSection = ({
  type,
}: SenderOrRecipientDetailsSectionProps) => {
  const { t } = useTranslation();

  const {
    isDisabledAccountNumber,
    isDisabledAccountOwnerName,
    isDisabledBrokerName,
    senderOrRecipientDetails,
    onChangeCustodianOrganization,
  } = useSenderOrRecipientDetailsSection({ type });

  const { field: custodianOrganizationField } = useField(
    `${type}Details.custodianOrganization`
  );

  const { field: brokerNameField } = useField(`${type}Details.brokerName`);

  const isEditable = useIsFormEditable();

  const custodianOrganizationLabel =
    custodianOrganizationField.value?.record?.isNew && !isEditable ? (
      <NewLabel
        label={t(
          'order.sections.senderOrRecipientDetailsSection.fields.custodianOrganization.label'
        )}
      />
    ) : (
      t(
        'order.sections.senderOrRecipientDetailsSection.fields.custodianOrganization.label'
      )
    );

  const brokerNameLabel =
    brokerNameField.value?.record?.isNew && !isEditable ? (
      <NewLabel
        label={t(
          'order.sections.senderOrRecipientDetailsSection.fields.brokerName.label'
        )}
      />
    ) : (
      t(
        'order.sections.senderOrRecipientDetailsSection.fields.brokerName.label'
      )
    );

  return (
    <OrderSection
      fields={lodash.compact([
        senderOrRecipientDetails ? (
          <DirectoryAutocompleteField<{ value: string; label: string }, false>
            fullWidth
            showRequiredMark
            directoryRecordFieldFilters={
              senderOrRecipientDetails.custodianOrganization
                .directoryRecordFieldFilters
            }
            directorySchemaName={
              senderOrRecipientDetails.custodianOrganization.directorySchemaName
            }
            fieldName={senderOrRecipientDetails.custodianOrganization.fieldName}
            label={custodianOrganizationLabel}
            name={`${type}Details.custodianOrganization`}
            placeholder={t(
              'order.sections.senderOrRecipientDetailsSection.fields.custodianOrganization.placeholder'
            )}
            onChange={onChangeCustodianOrganization}
          />
        ) : (
          <Field.Text
            fullWidth
            readOnly
            disabled={isEditable}
            label={custodianOrganizationLabel}
            name={`${type}Details.custodianOrganization.label`}
          />
        ),

        <Field.Text
          fullWidth
          disabled={!isEditable ? false : isDisabledAccountNumber}
          label={t(
            'order.sections.senderOrRecipientDetailsSection.fields.accountNumber.label'
          )}
          name={`${type}Details.accountNumber`}
          placeholder={
            !isDisabledAccountNumber
              ? t(
                  'order.sections.senderOrRecipientDetailsSection.fields.accountNumber.placeholder'
                )
              : undefined
          }
          showRequiredMark={!isDisabledAccountNumber}
        />,
        <Field.Text
          fullWidth
          disabled={!isEditable ? false : isDisabledAccountOwnerName}
          label={t(
            'order.sections.senderOrRecipientDetailsSection.fields.accountOwnerName.label'
          )}
          name={`${type}Details.accountOwnerName`}
          placeholder={
            !isDisabledAccountOwnerName
              ? t(
                  'order.sections.senderOrRecipientDetailsSection.fields.accountOwnerName.placeholder'
                )
              : undefined
          }
          showRequiredMark={!isDisabledAccountOwnerName}
        />,
        senderOrRecipientDetails ? (
          <DirectoryAutocompleteField<{ value: string; label: string }, false>
            fullWidth
            directorySchemaName={
              senderOrRecipientDetails.brokerName.directorySchemaName
            }
            disabled={!isEditable ? false : isDisabledBrokerName}
            fieldName={senderOrRecipientDetails.brokerName.fieldName}
            label={brokerNameLabel}
            name={`${type}Details.brokerName`}
            placeholder={t(
              'order.sections.senderOrRecipientDetailsSection.fields.brokerName.placeholder'
            )}
            showRequiredMark={!isDisabledBrokerName}
          />
        ) : (
          <Field.Text
            fullWidth
            readOnly
            disabled={isEditable}
            label={brokerNameLabel}
            name={`${type}Details.brokerName.label`}
          />
        ),
      ])}
      title={t(`order.sections.senderOrRecipientDetailsSection.title.${type}`)}
    />
  );
};
